import React, { useEffect, useState } from "react";
import { confirmable } from "react-confirm";
import { Modal, Row, Col, Input, InputNumber, Select, Switch, Button } from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { isEmpty } from "utils/GlobalFunctions";
import { apiGetAllUnits } from "services/shopUnitService";

const blankBracket = () => ({ min_weight_display: 0, max_weight_display: 100, charge: 0 });

function ZoneEditorDialog(props) {
  const { show, proceed, record, title } = props;

  const [data, setData] = useState({});
  const [brackets, setBrackets] = useState([]);
  const [errorField, setErrorField] = useState([]);
  // Weight units only — a zone's brackets are always a mass, so ml/pcs/etc.
  // never make sense as a bracket_unit_id.
  const [weightUnits, setWeightUnits] = useState([]);

  useEffect(() => {
    apiGetAllUnits()
      .then((units) => setWeightUnits((units || []).filter((u) => u.is_weight)))
      .catch(() => setWeightUnits([]));
  }, []);

  useEffect(() => {
    if (record !== undefined) {
      setData({
        ...record,
        packed_weight_factor_pct: Math.round(Number(record.packed_weight_factor || 0.75) * 100),
        countries: Array.isArray(record.countries_list) ? record.countries_list : [],
        surcharge_enabled: record.surcharge_enabled === "1",
        status: record.status === "1",
        bracket_unit_id: record.bracket_unit_id || null,
        max_weight_display: Number(record.max_weight_display ?? record.max_weight_g),
      });
      setBrackets(
        Array.isArray(record.brackets)
          ? record.brackets.map((b) => ({
              min_weight_display: Number(b.min_weight_display ?? b.min_weight_g),
              max_weight_display: Number(b.max_weight_display ?? b.max_weight_g),
              charge: Number(b.charge),
            }))
          : []
      );
    } else {
      setData({
        name: "",
        method_label: "",
        countries: [],
        packed_weight_factor_pct: 75,
        max_weight_display: 2000,
        bracket_unit_id: null,
        surcharge_enabled: false,
        surcharge_label: "ePacket Plus protection",
        surcharge_amount: 30,
        status: true,
      });
      setBrackets([blankBracket()]);
    }
  }, [record?.id]);

  const onChangeValue = (field, value) => {
    setData((d) => ({ ...d, [field]: value }));
    setErrorField((e) => e.filter((x) => x !== field));
  };

  // Grams represented by one unit of the given unit id (null/unrecognized = grams).
  // A base unit (g itself) has no `factor` (null) — that correctly falls back to 1.
  const gramsPerUnit = (unitId) => {
    if (!unitId) return 1;
    const u = weightUnits.find((x) => String(x.id) === String(unitId));
    const g = u ? Number(u.factor) : 1;
    return g > 0 ? g : 1;
  };

  const unitLabel = (unitId) => {
    if (!unitId) return "g";
    const u = weightUnits.find((x) => String(x.id) === String(unitId));
    return u ? u.name : "g";
  };

  // Switching units keeps the physical weight of every bracket (and the max
  // weight cutoff) the same — re-express the already-entered numbers in the
  // newly chosen unit instead of leaving stale numbers under a new label.
  const onChangeBracketUnit = (nextUnitId) => {
    const fromG = gramsPerUnit(data.bracket_unit_id);
    const toG = gramsPerUnit(nextUnitId);
    const convert = (v) => Math.round(((Number(v) || 0) * fromG) / toG * 10000) / 10000;
    setBrackets((list) =>
      list.map((b) => ({
        ...b,
        min_weight_display: convert(b.min_weight_display),
        max_weight_display: convert(b.max_weight_display),
      }))
    );
    setData((d) => ({ ...d, bracket_unit_id: nextUnitId, max_weight_display: convert(d.max_weight_display) }));
  };

  const updateBracket = (index, field, value) => {
    setBrackets((list) => list.map((b, i) => (i === index ? { ...b, [field]: value } : b)));
  };
  const addBracket = () => {
    const last = brackets[brackets.length - 1];
    const step = data.bracket_unit_id ? Number((100 / gramsPerUnit(data.bracket_unit_id)).toFixed(4)) : 100;
    const nextMin = last ? Number(last.max_weight_display) : 0;
    setBrackets((list) => [...list, { min_weight_display: nextMin, max_weight_display: nextMin + step, charge: 0 }]);
  };
  const removeBracket = (index) => setBrackets((list) => list.filter((_, i) => i !== index));

  const validate = () => {
    let errorList = [];
    errorList = isEmpty(data, "name", errorList);
    if (!Array.isArray(data.countries) || data.countries.length === 0) errorList = [...errorList, "countries"];
    if (brackets.length === 0) errorList = [...errorList, "brackets"];
    setErrorField(errorList);
    return errorList.length === 0;
  };

  return (
    <Modal
      open={show}
      destroyOnClose={true}
      onCancel={() => proceed(null)}
      closable={true}
      maskClosable={false}
      title={title}
      width={760}
      okText="Save"
      onOk={() => {
        if (!validate()) return;
        proceed({
          ...data,
          method_label: data.method_label || data.name,
          packed_weight_factor: Number(data.packed_weight_factor_pct || 75) / 100,
          max_weight_g: Math.round(Number(data.max_weight_display || 0) * gramsPerUnit(data.bracket_unit_id)),
          brackets,
        });
      }}
    >
      <Row gutter={16}>
        <Col xs={24} sm={12}>
          <div className="shop-field">
            <label className="shop-field-label">
              Zone name<span className="req">*</span>
            </label>
            <Input
              placeholder="e.g. Japan"
              value={data.name || ""}
              status={errorField.includes("name") ? "error" : ""}
              onChange={(e) => onChangeValue("name", e.target.value)}
            />
          </div>
        </Col>
        <Col xs={24} sm={12}>
          <div className="shop-field">
            <label className="shop-field-label">Customer-facing method label</label>
            <Input
              placeholder="e.g. Thailand Post ePacket - Japan"
              value={data.method_label || ""}
              onChange={(e) => onChangeValue("method_label", e.target.value)}
            />
          </div>
        </Col>
      </Row>

      <div className="shop-field">
        <label className="shop-field-label">
          Countries covered by this zone<span className="req">*</span>
        </label>
        <Select
          mode="tags"
          style={{ width: "100%" }}
          placeholder="Type a country and press Enter (e.g. Japan)"
          value={data.countries || []}
          onChange={(v) => onChangeValue("countries", v)}
          status={errorField.includes("countries") ? "error" : ""}
          tokenSeparators={[","]}
        />
      </div>

      <Row gutter={16} style={{ marginTop: 4 }}>
        <Col xs={24} sm={8}>
          <div className="shop-field">
            <label className="shop-field-label">Packed-weight factor</label>
            <InputNumber
              style={{ width: "100%" }}
              min={1}
              max={100}
              addonAfter="%"
              value={data.packed_weight_factor_pct}
              onChange={(v) => onChangeValue("packed_weight_factor_pct", v)}
            />
          </div>
        </Col>
        <Col xs={24} sm={8}>
          <div className="shop-field">
            <label className="shop-field-label">Max ePacket weight</label>
            <InputNumber
              style={{ width: "100%" }}
              min={0.0001}
              step={data.bracket_unit_id ? 0.01 : 1}
              addonAfter={unitLabel(data.bracket_unit_id)}
              value={data.max_weight_display}
              onChange={(v) => onChangeValue("max_weight_display", v)}
            />
          </div>
        </Col>
        <Col xs={24} sm={8}>
          <div className="shop-switch-row" style={{ marginTop: 22 }}>
            <label className="shop-field-label" style={{ marginBottom: 0 }}>
              Zone active
            </label>
            <Switch checked={!!data.status} onChange={(v) => onChangeValue("status", v)} />
          </div>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col xs={24} sm={8}>
          <div className="shop-switch-row">
            <label className="shop-field-label" style={{ marginBottom: 0 }}>
              Optional surcharge
            </label>
            <Switch checked={!!data.surcharge_enabled} onChange={(v) => onChangeValue("surcharge_enabled", v)} />
          </div>
        </Col>
        <Col xs={24} sm={8}>
          <div className="shop-field">
            <label className="shop-field-label">Surcharge label</label>
            <Input
              disabled={!data.surcharge_enabled}
              value={data.surcharge_label || ""}
              onChange={(e) => onChangeValue("surcharge_label", e.target.value)}
            />
          </div>
        </Col>
        <Col xs={24} sm={8}>
          <div className="shop-field">
            <label className="shop-field-label">Surcharge amount</label>
            <InputNumber
              style={{ width: "100%" }}
              min={0}
              step={0.01}
              disabled={!data.surcharge_enabled}
              value={data.surcharge_amount}
              onChange={(v) => onChangeValue("surcharge_amount", v)}
            />
          </div>
        </Col>
      </Row>

      <div className="shop-section-head" style={{ marginTop: 12 }}>
        <h4 className="shop-section-title">
          Weight brackets<span className="req">*</span>
        </h4>
        <Button className="shop-add-btn" icon={<PlusOutlined />} onClick={addBracket}>
          Add bracket
        </Button>
      </div>
      <div className="shop-field" style={{ maxWidth: 260 }}>
        <label className="shop-field-label">Enter/display brackets in</label>
        <Select
          style={{ width: "100%" }}
          value={data.bracket_unit_id || null}
          onChange={onChangeBracketUnit}
          options={[
            { value: null, label: "g (grams)" },
            ...weightUnits.filter((u) => u.name !== "g").map((u) => ({ value: u.id, label: u.name })),
          ]}
        />
        <div style={{ fontSize: 12, color: "var(--darkGreyColor)", marginTop: 4 }}>
          Rates are always calculated in grams internally — this only changes what unit you
          type the min/max weights in below (e.g. "1.5" for kg instead of "1500"), and the unit
          shown for Max ePacket weight above.
        </div>
      </div>
      {errorField.includes("brackets") && (
        <div className="shop-variant-error">Add at least one weight bracket.</div>
      )}
      {brackets.length > 0 && (
        <Row gutter={10} className="shop-variant-colhead">
          <Col span={7}>Min weight ({unitLabel(data.bracket_unit_id)})</Col>
          <Col span={7}>Max weight ({unitLabel(data.bracket_unit_id)})</Col>
          <Col span={8}>Customer charge</Col>
        </Row>
      )}
      <div style={{ maxHeight: 260, overflowY: "auto" }}>
        {brackets.map((b, index) => (
          <div className="shop-variant-row" key={index}>
            <Row gutter={10} align="middle">
              <Col span={7}>
                <InputNumber
                  style={{ width: "100%" }}
                  min={0}
                  step={data.bracket_unit_id ? 0.01 : 1}
                  value={b.min_weight_display}
                  onChange={(v) => updateBracket(index, "min_weight_display", v || 0)}
                />
              </Col>
              <Col span={7}>
                <InputNumber
                  style={{ width: "100%" }}
                  min={0}
                  step={data.bracket_unit_id ? 0.01 : 1}
                  value={b.max_weight_display}
                  onChange={(v) => updateBracket(index, "max_weight_display", v || 0)}
                />
              </Col>
              <Col span={8}>
                <InputNumber style={{ width: "100%" }} min={0} step={0.01} value={b.charge} onChange={(v) => updateBracket(index, "charge", v || 0)} />
              </Col>
            </Row>
            <Button type="text" className="shop-variant-del" icon={<DeleteOutlined />} onClick={() => removeBracket(index)} />
          </div>
        ))}
      </div>
    </Modal>
  );
}

export default confirmable(ZoneEditorDialog);
